import React from 'react';

/**
 * GalaxyBackground Component
 * Animated galaxy view with stars and particles for consistent branding
 * Used across all application pages for visual consistency
 */
export default function GalaxyBackground() {
  // Generate random stars
  const generateStars = (count) => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 2.5 + 0.5,
      opacity: Math.random() * 0.7 + 0.3,
      duration: Math.random() * 4 + 3,
      delay: Math.random() * 2,
    }));
  };

  // Generate golden particles
  const generateParticles = (count) => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 15 + 20,
      delay: Math.random() * 5,
      opacity: Math.random() * 0.5 + 0.2,
    }));
  };

  const stars = generateStars(120);
  const particles = generateParticles(60);

  return (
    <div className="fixed inset-0 z-0 overflow-hidden">
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: var(--star-opacity); }
          50% { opacity: calc(var(--star-opacity) * 0.3); }
        }

        @keyframes float-particle {
          0% {
            transform: translateY(100vh) translateX(0px);
            opacity: 0;
          }
          10% {
            opacity: var(--particle-opacity);
          }
          90% {
            opacity: var(--particle-opacity);
          }
          100% {
            transform: translateY(-100vh) translateX(100px);
            opacity: 0;
          }
        }

        @keyframes nebula-drift {
          0% { transform: translateX(0px) translateY(0px); }
          50% { transform: translateX(50px) translateY(-30px); }
          100% { transform: translateX(0px) translateY(0px); }
        }

        @keyframes cosmic-pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }

        .star {
          position: absolute;
          background: radial-gradient(circle, white, rgba(255,255,255,0.5));
          border-radius: 50%;
          animation: twinkle var(--duration, 3s) ease-in-out infinite;
          animation-delay: var(--delay, 0s);
        }

        .particle {
          position: absolute;
          animation: float-particle var(--duration, 20s) linear infinite;
          animation-delay: var(--delay, 0s);
        }
      `}</style>

      {/* Layer 1: Deep space base gradient */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, #0a0515 0%, #050505 50%, #000000 100%)',
        }}
      ></div>

      {/* Layer 2: Warm cosmic gradient from bottom */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to top, rgba(139, 69, 19, 0.1) 0%, transparent 40%)',
        }}
      ></div>

      {/* Layer 3: Blue-purple nebula from top */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, rgba(75, 0, 130, 0.08) 0%, transparent 50%)',
        }}
      ></div>

      {/* Layer 4: Golden top-right glow */}
      <div
        className="absolute -top-1/4 -right-1/4 w-1/2 h-1/2 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(255, 215, 0, 0.2) 0%, transparent 70%)',
          filter: 'blur(120px)',
          animation: 'cosmic-pulse 8s ease-in-out infinite',
        }}
      ></div>

      {/* Layer 5: Purple nebula fog center */}
      <div
        className="absolute top-1/4 left-1/3 w-96 h-96 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse, rgba(138, 43, 226, 0.12) 0%, transparent 70%)',
          filter: 'blur(100px)',
          animation: 'nebula-drift 25s ease-in-out infinite',
        }}
      ></div>

      {/* Layer 6: Cyan nebula bottom-left */}
      <div
        className="absolute -bottom-1/4 -left-1/4 w-1/2 h-1/2 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(0, 255, 200, 0.08) 0%, transparent 60%)',
          filter: 'blur(110px)',
          animation: 'nebula-drift 30s ease-in-out infinite reverse',
        }}
      ></div>

      {/* Layer 7: Stars */}
      <div className="absolute inset-0 pointer-events-none">
        {stars.map((star) => (
          <div
            key={`star-${star.id}`}
            className="star"
            style={{
              left: `${star.left}%`,
              top: `${star.top}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              '--duration': `${star.duration}s`,
              '--delay': `${star.delay}s`,
              '--star-opacity': star.opacity,
            }}
          ></div>
        ))}
      </div>

      {/* Layer 8: Golden particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map((particle) => (
          <div
            key={`particle-${particle.id}`}
            className="particle"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              '--duration': `${particle.duration}s`,
              '--delay': `${particle.delay}s`,
              '--particle-opacity': particle.opacity,
              background: `radial-gradient(circle, rgba(255, 215, 0, ${particle.opacity}), rgba(255, 215, 0, 0))`,
              borderRadius: '50%',
              boxShadow: `0 0 ${particle.size * 2}px rgba(255, 215, 0, ${particle.opacity * 0.8})`,
            }}
          ></div>
        ))}
      </div>

      {/* Layer 9: Vignette for depth */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 20%, rgba(0, 0, 0, 0.5) 100%)',
        }}
      ></div>

      {/* Layer 10: Subtle grid overlay for industrial touch */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `
            linear-gradient(0deg, rgba(255, 215, 0, 0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 215, 0, 0.5) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      ></div>
    </div>
  );
}
